import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Activity, AlertCircle, Loader } from 'lucide-react';
import Navbar from '@/components/Navbar';
import { api } from '@/utils/api';
import { useLanguage } from '@/LanguageContext';

const Interventions = ({ user, onLogout }) => {
  const { childId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [child, setChild] = useState(null);
  const [interventions, setInterventions] = useState([]);
  const [screenings, setScreenings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchData();
  }, [childId]);

  const fetchData = async () => {
    try {
      const [childRes, interventionsRes, screeningsRes] = await Promise.all([
        api.getChild(childId),
        api.getChildInterventions(childId),
        api.getChildScreenings(childId)
      ]);
      setChild(childRes.data);
      setInterventions(interventionsRes.data);
      setScreenings(screeningsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateIntervention = async (screeningId) => {
    setGenerating(true);
    try {
      await api.generateIntervention(screeningId);
      await fetchData();
    } catch (error) {
      console.error('Error generating intervention:', error);
      alert('Failed to generate intervention plan. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9]">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-96">
          <div className="text-xl font-bold text-[#0F4C81]">{t('common.loading')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#FAFAF9]" data-testid="interventions-page">
      <Navbar user={user} onLogout={onLogout} />

      <div className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate(`/child/${childId}`)}
          data-testid="back-button"
          className="flex items-center space-x-2 text-[#0F4C81] font-semibold mb-6 hover:underline"
        >
          <ArrowLeft size={20} />
          <span>{t('intervention.backToProfile')}</span>
        </button>

        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-[#111827] mb-2 font-['Nunito']" data-testid="interventions-title">
            {t('intervention.title')}
          </h1>
          <p className="text-lg text-[#4B5563]">
            {t('intervention.personalizedSupport')} <span className="font-bold">{child?.name}</span>
          </p>
        </div>

        {/* Generate New Intervention */}
        {screenings.length > 0 && (
          <div className="bg-[#EFF6FF] border-2 border-[#93C5FD] rounded-2xl p-6 mb-8" data-testid="generate-intervention-section">
            <div className="flex flex-col md:flex-row md:items-center justify-between">
              <div className="mb-4 md:mb-0">
                <h3 className="text-xl font-bold text-[#111827] mb-2 font-['Nunito']">{t('intervention.generateNew')}</h3>
                <p className="text-sm text-[#4B5563]">{t('intervention.createPersonalized')}</p>
              </div>
              <button
                onClick={() => handleGenerateIntervention(screenings[0].id)}
                data-testid="generate-plan-button"
                disabled={generating}
                className="px-8 py-3 bg-[#0F4C81] text-white rounded-full font-bold hover:bg-[#0d3d6b] transition-all active:scale-95 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {generating ? (
                  <span className="flex items-center space-x-2">
                    <Loader className="animate-spin" size={20} />
                    <span>{t('intervention.generating')}</span>
                  </span>
                ) : (
                  t('intervention.generate')
                )}
              </button>
            </div>
          </div>
        )}

        {/* No Screenings Alert */}
        {screenings.length === 0 && (
          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-2xl p-6 mb-8" data-testid="no-screenings-alert">
            <div className="flex items-start space-x-3">
              <AlertCircle className="text-[#F59E0B] flex-shrink-0" size={24} />
              <div>
                <h3 className="text-lg font-bold text-[#92400E] mb-2">{t('intervention.noScreeningData')}</h3>
                <p className="text-sm text-[#78350F] mb-4">
                  {t('intervention.completeScreeningFirst')}
                </p>
                <button
                  onClick={() => navigate(`/screening/${childId}`)}
                  data-testid="conduct-screening-button"
                  className="px-6 py-2 bg-[#F59E0B] text-black rounded-full font-bold hover:bg-[#D97706] transition-all active:scale-95"
                >
                  {t('intervention.conductScreening')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Intervention Plans List */}
        {interventions.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border-2 border-slate-100" data-testid="no-interventions-message">
            <Activity className="mx-auto text-[#9CA3AF] mb-4" size={64} />
            <p className="text-lg text-[#4B5563]">{t('intervention.noPlans')}</p>
          </div>
        ) : (
          <div className="space-y-6">
            {interventions.map((intervention, index) => (
              <div
                key={intervention.id}
                data-testid={`intervention-card-${intervention.id}`}
                className="rounded-2xl border-2 border-slate-100 bg-white p-8 shadow-sm"
              >
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 pb-6 border-b border-slate-200">
                  <div>
                    <h3 className="text-2xl font-bold text-[#111827] mb-2 font-['Nunito']">
                      {t('intervention.planNumber')} #{interventions.length - index}
                    </h3>
                    <div className="flex flex-wrap gap-3 text-sm text-[#4B5563]">
                      <span className="flex items-center space-x-1">
                        <Calendar size={16} />
                        <span>{t('intervention.created')}: {new Date(intervention.created_at).toLocaleDateString()}</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <Calendar size={16} />
                        <span>{t('intervention.followUp')}: {intervention.follow_up_date}</span>
                      </span>
                    </div>
                  </div>
                </div>

                {/* Risk Areas */}
                <div className="mb-6">
                  <h4 className="text-lg font-bold text-[#111827] mb-3 font-['Nunito']">{t('intervention.riskAreas')}</h4>
                  <div className="flex flex-wrap gap-2">
                    {intervention.risk_areas?.map((area, idx) => (
                      <span
                        key={idx}
                        className="px-4 py-2 bg-red-50 text-red-800 border-2 border-red-200 rounded-full text-sm font-semibold"
                      >
                        {area}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Recommendations */}
                <div className="mb-6">
                  <h4 className="text-lg font-bold text-[#111827] mb-3 font-['Nunito']">{t('intervention.recommendations')}</h4>
                  <div className="space-y-3">
                    {intervention.recommendations?.map((rec, idx) => (
                      <div
                        key={idx}
                        className="p-4 bg-[#F3F4F6] rounded-xl border-l-4 border-[#0F4C81]"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h5 className="font-bold text-[#111827]">{rec.title}</h5>
                          <span className={`text-xs px-3 py-1 rounded-full font-bold ${
                            rec.priority === 'high' ? 'bg-red-100 text-red-800' :
                            rec.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {rec.priority?.toUpperCase()}
                          </span>
                        </div>
                        <p className="text-sm text-[#4B5563]">{rec.description}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Activities */}
                <div>
                  <h4 className="text-lg font-bold text-[#111827] mb-3 font-['Nunito']">{t('intervention.activities')}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {intervention.activities?.map((activity, idx) => (
                      <div
                        key={idx}
                        className="p-4 bg-[#ECFDF5] rounded-xl border-2 border-[#A7F3D0]"
                      >
                        <h5 className="font-bold text-[#065F46] mb-2">{activity.name}</h5>
                        <p className="text-sm text-[#047857] mb-2">{activity.description}</p>
                        <div className="flex items-center justify-between text-xs text-[#047857]">
                          <span className="font-semibold">Frequency: {activity.frequency}</span>
                          <span className="font-semibold">{activity.duration_minutes} min</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Copyright Footer */}
      <footer className="bg-[#F3F4F6] border-t border-gray-200 py-3 text-center">
        <p className="text-sm text-[#6B7280]">© 2025 Hamrith Consulting Services LLP (HCS). All Rights Reserved.</p>
      </footer>
    </div>
  );
};

export default Interventions;
